// Identity, people directory, downloads and HubSpot for the Precon app, in the same shape as the
// services the app used when it ran as a Claude artifact.

export function createUserService(sb, session) {
  const u = session.user || {};
  const meta = u.user_metadata || {};
  const email = String(u.email || "").toLowerCase();
  const directory = {};
  return {
    // The app identifies people by account id; on this platform that's the lower-case sign-in email,
    // which is also how the rest of the Westbuilt suite identifies people.
    async me() {
      return {
        id: email || null, email: email || null,
        name: meta.full_name || meta.name || email,
        avatarUrl: meta.avatar_url || meta.picture || "",
        color: "#193441", isMe: true,
        // Settings are protected by the database rules, so the app doesn't need its own edit gate here.
        isOwner: false, canEdit: true,
      };
    },
    async profiles(ids) {
      const out = {};
      [].concat(ids || []).forEach(id => {
        const d = directory[id] || {};
        out[id] = { id, email: id, name: d.name || "", avatarUrl: "", color: "#193441", isMe: id === email };
      });
      return out;
    },
    // Westbuilt directory search (people who have access to the suite). Administrators only.
    async search(query) {
      const { data, error } = await sb.rpc("precon_directory_search", { p_query: String(query || "").slice(0, 80) });
      if (error) return [];
      return (data || []).map(p => {
        const id = String(p.email || "").toLowerCase();
        directory[id] = { name: p.name };
        return { id, email: id, name: p.name || id, avatarUrl: "", color: "#193441", isMe: id === email };
      });
    },
  };
}

export function createDownloads() {
  return {
    async save({ filename, data }) {
      if (!filename || data == null) throw { code: "bad_request", message: "Nothing to download" };
      const blob = data instanceof Blob ? data : new Blob([data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = String(filename).replace(/[\\/:*?"<>|]+/g, "-").slice(0, 200);
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      return { status: "saved" };
    },
  };
}

// HubSpot contact search runs server-side with the company token; the browser never sees it.
export function createHubspot(sb) {
  return {
    async callTool(server, tool, input) {
      if (server !== "HubSpot" || tool !== "search_crm_objects") throw { code: "bad_request", message: "Unsupported tool" };
      const { data: { session } } = await sb.auth.getSession();
      if (!session) throw { code: "needs_reauth", message: "Signed out" };
      // The app's own messages were written for the Claude-hosted version, so explain server-side
      // problems here with a toast and pass the app a code it shows as a plain "couldn't search".
      const explain = (msg, code) => { if (typeof window.showToast === "function") window.showToast(msg); return { code: code || "upstream_error", message: msg }; };
      let res;
      try {
        res = await fetch("/.netlify/functions/hubspot-search", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: "Bearer " + session.access_token },
          body: JSON.stringify({ query: String((input && input.query) || "").slice(0, 80) }),
        });
      } catch (e) { throw explain("Couldn't reach the HubSpot search service. Check your connection and try again."); }
      if (!res.ok) {
        let code = "", detail = "";
        try { const b = await res.json(); code = b.code || ""; detail = b.detail || ""; } catch (e) { /* not JSON */ }
        const messages = {
          not_configured: "HubSpot search isn't set up yet: the HubSpot token is missing on the server. An administrator needs to add HUBSPOT_TOKEN in Netlify and redeploy.",
          token_invalid: "The HubSpot token saved in Netlify isn't valid (it should start with pat-). An administrator needs to update HUBSPOT_TOKEN and redeploy.",
          token_rejected: "HubSpot rejected the app's token. It may be wrong, expired or missing the contacts permission. An administrator needs to check HUBSPOT_TOKEN.",
          sign_in: "Your sign-in has expired. Reload the page and sign in again.",
          not_member: "Your account doesn't have access to Precon's HubSpot search.",
          member_check_failed: "Couldn't confirm your access for HubSpot search. Try again in a moment.",
          busy: "HubSpot is busy. Try searching again in a moment.",
          server_error: "HubSpot search hit a server error" + (detail ? ": " + detail : ".") ,
        };
        if (res.status === 429 || code === "busy") throw { code: "rate_limited", message: messages.busy };
        throw explain(messages[code] || `HubSpot search failed (error ${res.status}). Try again, or enter the details manually.`, code === "not_member" ? "not_granted" : "upstream_error");
      }
      const body = await res.json();
      return { content: [], payload: { results: body.results || [], total: (body.results || []).length } };
    },
  };
}

// Estimating library: read-only queries against the `estimating` schema (plan-and-estimate
// catalog — Silver/Gold/Platinum template estimates). No write path yet.
export function createEstimatingService(sb) {
  const est = () => sb.schema("estimating");
  return {
    async listEstimates() {
      const { data, error } = await est()
        .from("estimates")
        .select(`
          id, tier, beds, study, bathroom, ensuite, kitchen, laundry,
          plans ( name, default_living_area ),
          estimate_contract_summary ( total_price )
        `)
        .eq("status", "template")
        .order("tier");
      if (error) throw { code: "upstream_error", message: error.message };
      return (data || []).map(e => ({
        id: e.id, tier: e.tier,
        planName: (e.plans || {}).name || "Unknown",
        livingArea: (e.plans || {}).default_living_area || null,
        beds: e.beds, study: e.study, bathroom: e.bathroom, ensuite: e.ensuite,
        kitchen: e.kitchen, laundry: e.laundry,
        totalPrice: (e.estimate_contract_summary || {}).total_price || null,
      }));
    },
    async getEstimate(id) {
      let q = est()
        .from("estimates")
        .select(`
          *, plans ( name, default_living_area ),
          estimate_modules ( * ),
          estimate_verandahs ( * ),
          estimate_wet_areas ( * ),
          estimate_window_selections ( quantity, glass_variant, screen_variant, priceable_components ( code ) ),
          estimate_cabinetry_selections ( quantity, priceable_components ( code, description ) ),
          estimate_input_cells ( sheet, cell, value ),
          estimate_stages ( name, sort_order ),
          estimate_stage_history ( id, stage_name, action, from_status, to_status, total_price, note, changed_by_name, changed_at ),
          estimate_line_items ( cost_code_group, sequence, description, category, detail, uom, quantity, unit_rate, line_total ),
          estimate_contract_summary ( * )
        `)
        .eq("id", id);
      // Only the timber door counts (Windows & Doors L5:L32) are needed from the stored cells.
      q = q.eq("estimate_input_cells.sheet", "Windows & Doors").like("estimate_input_cells.cell", "L%");
      q = q.order("changed_at", { foreignTable: "estimate_stage_history", ascending: false });
      q = q.order("module_number", { foreignTable: "estimate_modules", ascending: true });
      q = q.order("cost_code_group", { foreignTable: "estimate_line_items", ascending: true });
      const { data, error } = await q.single();
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },

    // Job estimates: copies of a template for a real job. Creating and editing are limited to Estimators and
    // Administrators (enforced in the database); the site fields allowed are validated there too.
    async canCreateEstimates() {
      const { data, error } = await est().rpc("can_create_estimates");
      return !error && data === true;
    },
    async listJobEstimates() {
      const { data, error } = await est()
        .from("estimates")
        .select(`
          id, tier, status, job_number, client_name, salesperson, updated_at, is_stale,
          plans ( name ), estimate_stages ( name ),
          estimate_contract_summary ( total_price )
        `)
        .neq("status", "template")
        .order("updated_at", { ascending: false });
      if (error) throw { code: "upstream_error", message: error.message };
      return (data || []).map(e => ({
        id: e.id, tier: e.tier, status: e.status, jobNumber: e.job_number, clientName: e.client_name,
        salesperson: e.salesperson, updatedAt: e.updated_at, isStale: e.is_stale, stageName: (e.estimate_stages || {}).name || "",
        planName: (e.plans || {}).name || "Unknown",
        totalPrice: (e.estimate_contract_summary || {}).total_price || null,
      }));
    },
    // Workflow: the stages Administrators define, and moving an estimate through them (issue, accept, return to draft, next stage).
    async listEstimateStages() {
      const { data, error } = await est().from("estimate_stages").select("id, name, sort_order, active, contract_stage").order("sort_order");
      if (error) throw { code: "upstream_error", message: error.message };
      return data || [];
    },
    async saveEstimateStage(id, name, active) {
      const { data, error } = await est().rpc("save_estimate_stage", { p_id: id || null, p_name: name, p_active: active !== false });
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },
    async moveEstimateStage(id, direction) {
      const { error } = await est().rpc("move_estimate_stage", { p_id: id, p_direction: direction });
      if (error) throw { code: "upstream_error", message: error.message };
    },
    async deleteEstimateStage(id) {
      const { error } = await est().rpc("delete_estimate_stage", { p_id: id });
      if (error) throw { code: "upstream_error", message: error.message };
    },
    // Contracts (Phase 5): the stage a contract is priced from (Administrators), and whether an estimate is ready for a contract.
    async setContractStage(id) {
      const { error } = await est().rpc("set_contract_stage", { p_id: id });
      if (error) throw { code: "upstream_error", message: error.message };
    },
    async contractHandoffForEstimate(id) {
      const { data, error } = await sb.rpc("precon_contract_handoff_for_estimate", { p_estimate: id });
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },
    async setEstimateState(id, action, note) {
      const { data, error } = await est().rpc("set_estimate_state", { p_id: id, p_action: action, p_note: note || null });
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },
    // Rate card (Administrators only, enforced in the database): fixed line rates, exact price preview, change sets with an
    // audit log, revert, and recalculation of the estimates a change flags.
    async listRateItems() {
      const { data, error } = await est().from("rate_items").select("id, cost_code_group, sequence, detail, uom, base_cost, sell_cost, rate_source")
        .order("cost_code_group").order("sequence");
      if (error) throw { code: "upstream_error", message: error.message };
      return data || [];
    },
    async listCostCodes() {
      const { data, error } = await est().from("cost_codes").select("group_code, group_label, category").order("group_code");
      if (error) throw { code: "upstream_error", message: error.message };
      return data || [];
    },
    async previewRateChanges(changes) {
      const { data, error } = await est().rpc("preview_rate_changes", { p_changes: changes });
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },
    async applyRateChanges(changes, reason) {
      const { data, error } = await est().rpc("apply_rate_changes", { p_changes: changes, p_reason: reason });
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },
    async revertRateBatch(id) {
      const { data, error } = await est().rpc("revert_rate_batch", { p_batch_id: id });
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },
    async listRateBatches() {
      const { data, error } = await est().from("rate_change_batches").select("id, reason, applied_by_name, applied_at, item_count, reverts_batch_id")
        .eq("kind", "rate").order("applied_at", { ascending: false }).limit(25);
      if (error) throw { code: "upstream_error", message: error.message };
      return data || [];
    },
    async getRateBatch(id) {
      const { data, error } = await est().from("rate_change_log").select("id, cost_code_group, sequence, detail, old_rate, new_rate").eq("batch_id", id)
        .order("cost_code_group").order("sequence");
      if (error) throw { code: "upstream_error", message: error.message };
      return data || [];
    },
    // Window and cabinetry prices (Administrators only): the same change-set flow as the line rates.
    async listComponentPrices() {
      const { data, error } = await est().from("component_variant_rates").select("id, component_id, axis_name, variant_label, price, priceable_components ( code, description, category )");
      if (error) throw { code: "upstream_error", message: error.message };
      return data || [];
    },
    async previewComponentPriceChanges(changes) {
      const { data, error } = await est().rpc("preview_component_price_changes", { p_changes: changes });
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },
    async applyComponentPriceChanges(changes, reason) {
      const { data, error } = await est().rpc("apply_component_price_changes", { p_changes: changes, p_reason: reason });
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },
    async revertComponentBatch(id) {
      const { data, error } = await est().rpc("revert_component_batch", { p_batch_id: id });
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },
    async listComponentBatches() {
      const { data, error } = await est().from("rate_change_batches").select("id, reason, applied_by_name, applied_at, item_count, reverts_batch_id")
        .eq("kind", "component").order("applied_at", { ascending: false }).limit(25);
      if (error) throw { code: "upstream_error", message: error.message };
      return data || [];
    },
    async getComponentBatch(id) {
      const { data, error } = await est().from("component_price_change_log").select("id, component_code, category, axis_name, variant_label, old_price, new_price").eq("batch_id", id).order("component_code");
      if (error) throw { code: "upstream_error", message: error.message };
      return data || [];
    },
    // Formulas (read only): every line's quantity rule, and one estimate's quantities for each line.
    async listRules() {
      const { data, error } = await est().from("rate_item_quantity_rules").select("rate_item_id, rule, notes");
      if (error) throw { code: "upstream_error", message: error.message };
      return data || [];
    },
    // Formula editor (Administrators only, enforced in the database). A change can only be applied after a complete, error-free
    // safety test of that exact formula; the test never saves anything.
    async testRuleChange(itemId, rule, runId, offset) {
      const { data, error } = await est().rpc("test_rule_change", { p_item: itemId, p_rule: rule, p_run: runId || null, p_offset: offset || 0 });
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },
    async applyRuleChange(itemId, rule, oldRule, reason, runId) {
      const { data, error } = await est().rpc("apply_rule_change", { p_item: itemId, p_rule: rule, p_old: oldRule, p_reason: reason, p_run: runId });
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },
    async revertRuleChange(logId) {
      const { data, error } = await est().rpc("revert_rule_change", { p_log_id: logId });
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },
    async listRuleChanges(itemId) {
      let q = est().from("rule_change_log").select("id, rate_item_id, cost_code_group, sequence, detail, old_rule, new_rule, reason, changed_by_name, changed_at, reverts_log_id").order("changed_at", { ascending: false }).limit(1000);
      if (itemId) q = q.eq("rate_item_id", itemId);
      const { data, error } = await q;
      if (error) throw { code: "upstream_error", message: error.message };
      return data || [];
    },
    // The lines whose formula now differs from the baseline captured from the Excel workbook (Administrators only; empty for anyone else).
    async listRuleBaselineDiff() {
      const { data, error } = await est().from("rule_baseline_diff").select("rate_item_id, original_rule, current_rule");
      if (error) throw { code: "upstream_error", message: error.message };
      return data || [];
    },
    // Custom estimates (Estimators and Administrators, draft job estimates, enforced in the database): unlock with a reason, then edit the structure tables.
    async getCustomEstimate(id) {
      const [e, m, v, g, on, wet, floor] = await Promise.all([
        est().from("estimates").select("status, is_custom, custom_since, custom_by_name, custom_reason, beds, study, bathroom, ensuite, washroom, wc, wir, wil_wip, kitchen, laundry").eq("id", id).single(),
        est().from("estimate_modules").select("module_number, length, width, verandah_area, roof_type, double_eave, valley_hip_dutch, verandah_style, external_walls").eq("estimate_id", id).order("module_number"),
        est().from("estimate_verandahs").select("type, length, width, external_walls, roof_type, style, slot").eq("estimate_id", id).not("slot", "is", null).order("slot"),
        est().from("estimate_input_cells").select("value").eq("estimate_id", id).eq("sheet", "Onsite Verandahs").eq("cell", "G19").maybeSingle(),
        est().rpc("onsite_state", { p_estimate: id }),
        est().rpc("wet_state", { p_estimate: id }),
        est().rpc("floor_state", { p_estimate: id }),
      ]);
      if (e.error) throw { code: "upstream_error", message: e.error.message };
      if (m.error) throw { code: "upstream_error", message: m.error.message };
      if (v.error) throw { code: "upstream_error", message: v.error.message };
      if (on.error) throw { code: "upstream_error", message: on.error.message };
      if (wet.error) throw { code: "upstream_error", message: wet.error.message };
      if (floor.error) throw { code: "upstream_error", message: floor.error.message };
      const { status, is_custom, custom_since, custom_by_name, custom_reason, ...rooms } = e.data;
      return { status, is_custom, custom_since, custom_by_name, custom_reason, rooms, modules: m.data || [], verandahs: { rows: v.data || [], double_gable: g && g.data && g.data.value != null && g.data.value !== "" ? Number(g.data.value) : 0 }, onsite: on.data || {}, wet: wet.data || {}, floor: floor.data || {} };
    },
    async unlockCustomEstimate(id, reason) {
      const { data, error } = await est().rpc("unlock_custom_estimate", { p_estimate: id, p_reason: reason });
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },
    // Electrical and Plumbing quantities: the calculated and overridden quantity of each item, saving one section's overrides, and bringing quantities left behind by earlier edits up to date.
    async getEpState(id) {
      const { data, error } = await est().rpc("ep_state", { p: id });
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },
    async saveEp(id, section, changes) {
      const { data, error } = await est().rpc("save_ep", { p_estimate: id, p_section: section, p_changes: changes });
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },
    async refreshEp(id) {
      const { data, error } = await est().rpc("refresh_ep", { p_estimate: id });
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },
    async saveFloor(id, state) {
      const { data, error } = await est().rpc("save_floor", { p_estimate: id, p_state: state });
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },
    async saveWet(id, rows) {
      const { data, error } = await est().rpc("save_wet", { p_estimate: id, p_rows: rows });
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },
    async saveOnsite(id, state) {
      const { data, error } = await est().rpc("save_onsite", { p_estimate: id, p_state: state });
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },
    async saveVerandahs(id, rows, doubleGable) {
      const { data, error } = await est().rpc("save_verandahs", { p_estimate: id, p_rows: rows, p_gable: doubleGable || 0 });
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },
    async saveRoomCounts(id, counts) {
      const { data, error } = await est().rpc("save_room_counts", { p_estimate: id, p_counts: counts });
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },
    async saveModules(id, rows) {
      const { data, error } = await est().rpc("save_modules", { p_estimate: id, p_rows: rows });
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },
    // Custom price lists (Estimators and Administrators, enforced in the database): calculate, save and read.
    async listPlans() {
      const { data, error } = await est().from("plans").select("id, name").order("name");
      if (error) throw { code: "upstream_error", message: error.message };
      return data || [];
    },
    async startCustomPrices(planIds, levels, details) {
      const { data, error } = await est().rpc("start_custom_prices", { p_plan_ids: planIds, p_levels: levels, p_details: details || {} });
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },
    async runCustomPrices(runId, offset) {
      const { data, error } = await est().rpc("run_custom_prices", { p_run: runId, p_offset: offset || 0 });
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },
    async getCustomRunLines(runId) {
      const { data, error } = await est().rpc("custom_price_run_lines", { p_run: runId });
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },
    async saveCustomPricelist(runId, name, notes) {
      const { data, error } = await est().rpc("save_custom_pricelist", { p_run: runId, p_name: name, p_notes: notes || null });
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },
    async listCustomPricelists() {
      const { data, error } = await est().from("custom_pricelists").select("id, name, notes, levels, created_by_name, created_at").order("created_at", { ascending: false });
      if (error) throw { code: "upstream_error", message: error.message };
      return data || [];
    },
    async getCustomPricelist(id) {
      const [l, ln] = await Promise.all([
        est().from("custom_pricelists").select("id, name, notes, details, levels, created_by_name, created_at").eq("id", id).single(),
        est().from("custom_pricelist_lines").select("plan_id, plan_name, sort_order, beds, bathrooms, ensuites, living_area, verandah_area, total_area, silver_price, gold_price, platinum_price").eq("list_id", id).order("sort_order"),
      ]);
      if (l.error) throw { code: "upstream_error", message: l.error.message };
      if (ln.error) throw { code: "upstream_error", message: ln.error.message };
      return Object.assign({}, l.data, { lines: ln.data || [] });
    },
    async deleteCustomPricelist(id) {
      const { error } = await est().rpc("delete_custom_pricelist", { p_id: id });
      if (error) throw { code: "upstream_error", message: error.message };
    },
    // Cost lines with their adjustments, and saving adjustments (Estimators and Administrators, draft job estimates; the reason is required).
    async getEstimateLines(id) {
      const [l, e] = await Promise.all([
        est().from("estimate_line_items").select("rate_item_id, cost_code_group, sequence, description, category, detail, uom, quantity, unit_rate, line_total, adjustment, adjustment_reason").eq("estimate_id", id).order("cost_code_group").order("sequence"),
        est().from("estimates").select("status").eq("id", id).single(),
      ]);
      if (l.error) throw { code: "upstream_error", message: l.error.message };
      if (e.error) throw { code: "upstream_error", message: e.error.message };
      return { status: e.data.status, lines: l.data || [] };
    },
    async setLineAdjustments(estimateId, changes) {
      const { data, error } = await est().rpc("set_line_adjustments", { p_estimate: estimateId, p_changes: changes });
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },
    async getLineQuantities(estimateId) {
      const { data, error } = await est().from("estimate_line_items").select("rate_item_id, quantity, unit_rate, line_total").eq("estimate_id", estimateId);
      if (error) throw { code: "upstream_error", message: error.message };
      return data || [];
    },
    // Recalculates up to `limit` stale templates and drafts and returns how many are still stale.
    async recomputeStale(limit) {
      const { data, error } = await est().rpc("recompute_stale_estimates", { p_limit: limit || 12 });
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },
    // Window/door types and cabinetry items an estimator can choose from (code, unit and category only).
    async listComponents() {
      const { data, error } = await est().from("priceable_components").select("code, description, category").order("code");
      if (error) throw { code: "upstream_error", message: error.message };
      return (data || []).map(c => ({ code: String(c.code).trim(), unit: c.description || "", category: c.category }));
    },
    // Precon jobs to link an estimate to (code and client name only). Empty if the person can't read them.
    async listPreconJobs() {
      const { data, error } = await sb.from("precon_jobs").select("code, client_name, type")
        .eq("archived", false).order("code", { ascending: false }).limit(300);
      return error ? [] : (data || []);
    },
    async createEstimateFromTemplate(templateId, details) {
      const { data, error } = await est().rpc("create_estimate_from_template", { p_template_id: templateId, p_details: details || {} });
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },
    async updateEstimate(id, changes) {
      const { error } = await est().rpc("update_estimate", { p_id: id, p_changes: changes || {} });
      if (error) throw { code: "upstream_error", message: error.message };
    },

    // Pricelist: saved, versioned snapshots of every design's price by inclusions level.
    // Anyone with Estimating access can read; only Administrators can generate (enforced in the database).
    async isAdmin() {
      const { data, error } = await est().rpc("is_estimating_admin");
      return !error && data === true;
    },
    async listPricelists() {
      const { data, error } = await est()
        .from("pricelists")
        .select("id, version_number, generated_at, generated_by_name, notes")
        .is("deleted_at", null)
        .order("version_number", { ascending: false });
      if (error) throw { code: "upstream_error", message: error.message };
      return data || [];
    },
    async getPricelist(id) {
      const { data, error } = await est()
        .from("pricelists")
        .select("id, version_number, generated_at, generated_by_name, notes, pricelist_lines ( * )")
        .eq("id", id)
        .single();
      if (error) throw { code: "upstream_error", message: error.message };
      data.pricelist_lines = (data.pricelist_lines || []).slice().sort((a, b) => a.sort_order - b.sort_order);
      return data;
    },
    // The number the next generated version will get (deleted versions still count, so numbers are never reused).
    async nextPricelistVersion() {
      const { data, error } = await est().rpc("next_pricelist_version");
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },
    // Administrators only (enforced in the database). Deleting keeps an audit record and never reuses the number.
    async deletePricelist(id, reason) {
      const { data, error } = await est().rpc("delete_pricelist", { p_id: id, p_reason: reason || null });
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },
    // Deletes every version newer than the chosen one, so the chosen version becomes the current one.
    async rollbackPricelist(id, reason) {
      const { data, error } = await est().rpc("rollback_pricelist", { p_id: id, p_reason: reason || null });
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },
    async generatePricelist(notes) {
      const { data, error } = await est().rpc("generate_pricelist", { p_notes: notes || null });
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },
  };
}
